import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatReportDate(dateString: string | null | undefined, formatStr: string = "MMM dd, yyyy"): string {
  if (!dateString) return "—";
  try {
    const parts = dateString.split("-");
    if (parts.length < 3) return dateString;
    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return dateString;
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return dateString;
    return format(date, formatStr);
  } catch {
    return dateString || "—";
  }
}

/**
 * Copies text to the clipboard. Uses the modern Clipboard API with a
 * textarea execCommand fallback for older browsers.
 * Returns true on success, false on failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
}

// Gate F18.3: Parse UTC timestamp and format in user's local timezone
// Handles timestamps from PostgreSQL that may lack 'Z' suffix
export function formatUtcTimestamp(isoTimestamp: string | null, formatStr: string = "MMM dd, yyyy 'at' h:mm a"): string {
  if (!isoTimestamp) return "—";
  try {
    // Ensure timestamp is interpreted as UTC by appending 'Z' if not present
    let utcTimestamp = isoTimestamp;
    if (!isoTimestamp.endsWith('Z') && !isoTimestamp.includes('+') && !isoTimestamp.includes('-', 10)) {
      utcTimestamp = isoTimestamp + 'Z';
    }
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) return "—";
    // format() displays in the browser's local timezone
    return format(date, formatStr);
  } catch {
    return "—";
  }
}
